import React, { useMemo } from 'react'
import { type GridColumns } from '@retailer-platform/shared-components'
import { Text } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { type ProductsSearchDatum } from 'domains/products/components/search/utils/types'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import { ProductsSearchTableDeptAisleCell } from 'domains/products/components/search/components/table/components/dept-aisle-cell/ProductsSearchTableDeptAisleCell'
import { ProductsSearchTableProductCodesCell } from 'domains/products/components/search/components/table/components/product-codes-cell/ProductsSearchTableProductCodesCell'
import { ProductsSearchTableSelectionCell } from 'domains/products/components/search/components/table/components/selection-cell/ProductsSearchTableSelectionCell'
import { ProductsSearchTableSelectionHeader } from 'domains/products/components/search/components/table/components/selection-header/ProductsSearchTableSelectionHeader'
import { ProductsSearchTablePriceCell } from 'domains/products/components/search/components/table/components/price-cell/ProductsSearchTablePriceCell'
import { ProductsSearchTableLazyDataCell } from 'domains/products/components/search/components/table/components/lazy-data-cell/ProductsSearchTableLazyDataCell'
import { ProductsSearchTableProductNameCell } from 'domains/products/components/search/components/table/components/product-name-cell/ProductsSearchTableProductNameCell'
import { ProductsEditButtonAndModal } from 'domains/products/components/search/components/edit-product-button-and-modal/ProductsEditButtonAndModal'
import { ProductsSearchTableDateCell } from 'domains/products/components/search/components/table/components/date-cell/ProductsSearchTableDateCell'

interface Props {
  itemsLoading: boolean
  itemsError: boolean
  localeCode: string
  refetchItemsData?: () => void
  showInAssortment: boolean
}

const CellText = styled(({ children, ...props }) => (
  <Text {...props} title={children}>
    {children}
  </Text>
))({
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

export const useProductsSearchListColumns = ({
  itemsLoading,
  itemsError,
  localeCode,
  refetchItemsData,
  showInAssortment,
}: Props) => {
  const columnHeaders = useDomainMessages({
    productDisplayName: 'productsDomain.search.columns.productDisplayName',
    upc: 'productsDomain.search.columns.upc',
    rrc: 'productsDomain.search.columns.rrcsku',
    primaryDeptAisle: 'productsDomain.search.columns.primaryDeptAisle',
    priceLow: 'productsDomain.search.columns.priceLow',
    priceHigh: 'productsDomain.search.columns.priceHigh',
    availability: 'productsDomain.search.columns.availability',
    created: 'productsDomain.search.columns.created',
  })

  const columns: GridColumns<ProductsSearchDatum> = useMemo(() => {
    const lazyDataProps = {
      loading: itemsLoading,
      error: itemsError,
      refetch: refetchItemsData,
    }

    return [
      {
        id: 'select',
        Header: ProductsSearchTableSelectionHeader,
        Cell: ({ row }) => <ProductsSearchTableSelectionCell productId={row.original.productId} />,
        width: 42,
        sticky: 'left',
      },
      {
        id: 'productDisplayName',
        Header: columnHeaders.productDisplayName,
        Cell: ({ row }) => (
          <ProductsSearchTableProductNameCell productId={row.original.productId}>
            <CellText weight={'semibold'}>{row.original.productDisplayName}</CellText>
          </ProductsSearchTableProductNameCell>
        ),
        width: 300,
        sticky: 'left',
      },
      {
        id: 'upc',
        Header: columnHeaders.upc,
        Cell: ({ row }) => (
          <ProductsSearchTableProductCodesCell productCodes={row.original.productCodes} />
        ),
      },
      {
        id: 'rrc',
        Header: columnHeaders.rrc,
        Cell: ({ row }) => <Text>{row.original.retailerReferenceCode ?? '-'}</Text>,
      },
      {
        id: 'primaryDeptAisle',
        Header: columnHeaders.primaryDeptAisle,
        Cell: ({ row }) => (
          <ProductsSearchTableDeptAisleCell
            departments={row.original.departments}
            aisles={row.original.aisles}
            primaryDepartmentId={row.original.primaryDepartmentId}
            primaryAisleId={row.original.primaryAisleId}
          />
        ),
        width: 250,
      },
      {
        id: 'priceLow',
        Header: columnHeaders.priceLow,
        Cell: ({ row }) => (
          <ProductsSearchTableLazyDataCell {...lazyDataProps}>
            <ProductsSearchTablePriceCell priceCents={row.original.minPriceCents} />
          </ProductsSearchTableLazyDataCell>
        ),
      },
      {
        id: 'priceHigh',
        Header: columnHeaders.priceHigh,
        Cell: ({ row }) => (
          <ProductsSearchTableLazyDataCell {...lazyDataProps}>
            <ProductsSearchTablePriceCell priceCents={row.original.maxPriceCents} />
          </ProductsSearchTableLazyDataCell>
        ),
      },
      {
        id: 'availability',
        Header: columnHeaders.availability,
        Cell: ({ row }) => (
          <ProductsSearchTableLazyDataCell {...lazyDataProps}>
            <Text>{row.original.availableItemsCount}</Text>
          </ProductsSearchTableLazyDataCell>
        ),
      },
      {
        id: 'created',
        Header: columnHeaders.created,
        Cell: ({ row }) => <ProductsSearchTableDateCell date={row.original.createdAt} />,
      },
      {
        id: 'contextMenu',
        Cell: ({ row }) => (
          <ProductsEditButtonAndModal
            productId={row.original.productId}
            productName={row.original.productDisplayName}
            productUpc={row.original.productCodes?.[0]}
            localeCode={localeCode}
            showInAssortment={showInAssortment}
          />
        ),
        width: 50,
        sticky: 'right',
      },
    ]
  }, [columnHeaders, itemsError, itemsLoading, localeCode, refetchItemsData, showInAssortment])

  return columns
}
